import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { UnitService } from '../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../communication/project-comms.service';
import { FeedstockFinancialPropName } from '../models/project/feedstock/feedstock-financial.model';
import { Feedstock, FeedstockPropName } from '../models/project/feedstock/feedstock.model';
import { FinancialCapex, FinCapexPropName } from '../models/project/financial/financial-capex.model';
import { FinancialFeeType } from '../models/project/financial/financial-fee-type.enum';
import { FinancialIncome, FinancialIncomePropName } from '../models/project/financial/financial-income.model';
import { FinancialInvestment, FinancialInvestmentPropName } from '../models/project/financial/financial-investment/financial-investment.model';
import { FinancialOpex, FinOpexPropName } from '../models/project/financial/financial-opex.model';
import { Loan, LoanPropName } from '../models/project/financial/financial-investment/loan.model';
import { FinancialPropName } from '../models/project/financial/financial.model';
import { CarbonCreditPropName } from '../models/project/ghg-emission/carbon-credit.model';
import { EmissionRef } from '../models/project/ghg-emission/emission.model';
import { GhgEmissionsPropName } from '../models/project/ghg-emission/ghg-emissions.model';
import { TransportPropName } from '../models/project/ghg-emission/transportation.model';
import { Fuel, FuelPropName, PeriodType } from '../models/project/plant-specifications/fuel.model';
import { PlantSpecifications, PlantSpecificationsPropName } from '../models/project/plant-specifications/plant-specifications.model';
import { Product, ProductPropName, ProductType } from '../models/project/plant-specifications/product.model';
import { Project, ProjectPropName } from '../models/project/project.model';
import { FeedstockQuantitiesPropName } from '../models/project/feedstock/feedstock-quantities.model';
import { FeedstockCharacteristicsPropName } from '../models/project/feedstock/feedstock-characteristics.model';
import { FeedstockBmpPotentialCodPropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-cod.model';
import { FeedstockNpkPropName } from '../models/project/feedstock/feedstock-npk.model';
import { FeedstockBmpType } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockBmpPotentialDirectPropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-direct.model';
import { FeedstockBmpPotentialYieldPropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-yield.model';
import { FeedstockBmpPotentialNutritionPropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-nutrient.model';
import { FeedstockBmpPotentialElementalPropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-elemental.model';
import { FeedstockBmpCurveItem, FeedstockBmpCurvePropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-curve.model';
import Utils from '../util';
import { LcfsRinPropName } from '../models/project/lcfs-rin.model';
import { BehaviorSubject } from 'rxjs';
import { EnergyGenerationUnit, EnergyGenPropName } from '../models/project/plant-specifications/energy-generation-unit.model';
import { GasUpgradingPropName, GasUpgradingUnit } from '../models/project/plant-specifications/gas-upgrading-unit.model';
import { FeedstockBmpPotentialAI1PropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI1.model';
import { FeedstockBmpPotentialAI2PropName } from '../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI2.model';
import { AdditionalProduct, AddProdPropName } from '../models/project/plant-specifications/additional-product.model';
import { ProjectInputItem } from '../models/data-structures/project/project-input-item.model';
import { BiogasShare } from '../models/project/plant-specifications/biogas-share.model';

@Injectable({
  providedIn: 'root'
})
export class ProjectService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  project = new Project();
  currentResultsProject: Project;
  latestResultProject: Project;

  projectFunctionalityReadyNotifier = new BehaviorSubject(false);
  currentResultsFunctionalityReadyNotifier = new BehaviorSubject(false);
  latestResultsFunctionalityReadyNotifier = new BehaviorSubject(false);

  private unitsReady = false;
  private projectReady = false;
  private resultsReady = false;
  private latestResultsReady = false;

  emissionNames = {
    electricEmission: GhgEmissionsPropName.electricEmission,
    natgasEmission: GhgEmissionsPropName.natgasEmission,
    fertilizerEmission: GhgEmissionsPropName.fertilizerEmission,
    biomethEmission: GhgEmissionsPropName.biomethEmission,
    landfillEmission: GhgEmissionsPropName.landfillEmission,
    ssoEmission: GhgEmissionsPropName.ssoEmission,
    mwEmission: GhgEmissionsPropName.mwEmission,
    agriEmission: GhgEmissionsPropName.agriEmission,
    forestEmission: GhgEmissionsPropName.forestEmission,
    municipalEmission: GhgEmissionsPropName.municipalEmission
  };

  constructor(
    private projectCommService: ProjectCommunicationService,
    private unitService: UnitService,
  ) {
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.projectReady = loaded;
        if (loaded) {
          this.project = this.projectCommService.getProject();
        } else {
          this.project = null;
        }
        this.updateProjectFunctionalityNotifier();
      });
    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.project = this.projectCommService.getProject();
        } else {
          this.project = null;
        }
      });
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.resultsReady = loaded;
        if (loaded) {
          this.currentResultsProject = this.projectCommService.getResultProjectState();
        } else {
          this.currentResultsProject = null;
        }
        this.updateCurrentResultFunctionalityNotifier();
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.unitsReady = loaded;
        this.updateProjectFunctionalityNotifier();
        this.updateCurrentResultFunctionalityNotifier();
        this.updateLatestResultFunctionalityNotifier();
      });
  }

  updateProjectFunctionalityNotifier(): void {
    this.projectFunctionalityReadyNotifier.next(this.projectReady && this.unitsReady);
    this.loadLatestResultSet();
  }

  updateCurrentResultFunctionalityNotifier(): void {
    this.currentResultsFunctionalityReadyNotifier.next(this.resultsReady && this.unitsReady);
    this.loadLatestResultSet();
  }

  updateLatestResultFunctionalityNotifier(): void {
    this.latestResultsFunctionalityReadyNotifier.next(this.latestResultsReady && this.unitsReady);
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  loadLatestResultSet() {
    if (this.projectReady && this.resultsReady) {
      this.projectCommService.fetchLatestResultSet()
        .pipe(take(1))
        .subscribe(result => {
          if (!result || !result['project-state']) {
            throw Error('failed to fetch latest results');
          }
          this.latestResultProject = result['project-state'];
          this.latestResultsReady = true;
          this.updateLatestResultFunctionalityNotifier();
        });
    } else {
      this.latestResultsReady = false;
      this.latestResultProject = null;
      this.updateLatestResultFunctionalityNotifier();
    }

  }

  getInputSummary() {
    if (this.project) {
      return this.project.getInputSummary();
    }
  }

  getResultProjectInputSummary() {
    if (this.currentResultsProject) {
      return this.currentResultsProject.getInputSummary();
    }
  }

  getDefaultCurrency() {
    if (this.project && this.project.projectInfo && this.project.projectInfo.defaults) {
      return this.project.projectInfo.defaults.currency;
    }
  }

  getLatestResultsBiogasShareData(): BiogasShare[] {
    return this.latestResultProject.plantSpecifications.biogasShareData;
  }

  /*
  *  main sections ----------------------------------------------------
  */

  getNewProjectInputs() {
    const inputs: ProjectInputItem[] = [];
    inputs.push(...this.getNewFeedstockInputs());
    inputs.push(...this.getNewPlantSpecInputs());

    if (this.project.projectInfo.useFinancial) {
      inputs.push(...this.getNewFinancialInputs());
    }
    return inputs;
  }

  getNewFeedstockInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getFeedstockComparisonInputs(this.project, isSensitivity);
  }

  getCurrentResultFeedstockInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getFeedstockComparisonInputs(this.currentResultsProject, isSensitivity);
  }

  getLatestResultFeedstockInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getFeedstockComparisonInputs(this.latestResultProject, isSensitivity);
  }

  private getFeedstockComparisonInputs(project: Project, isSensitivity: boolean): ProjectInputItem[] {
    if (!project) return [];
    const ProjectInputItems: ProjectInputItem[] = [];

    for (const feedstock of project.feedstocks) {
      // mainly for old projects that do not have uuid's, or if something is added that doesn't use them
      const itemPathIdentifier = (!feedstock.uuid ? project.feedstocks.indexOf(feedstock) : feedstock.getUuidForPath());
      const path = ['feedstocks', '' + itemPathIdentifier];
      ProjectInputItems.push(...this.getFeedstockQuantitySensItems(feedstock, path));
      ProjectInputItems.push(...this.getFeedstockCharacteristicSensItems(feedstock, path));
      ProjectInputItems.push(...this.getFeedstockBmpPotentialSensItems(feedstock, path, isSensitivity));
      if (!isSensitivity) {
        ProjectInputItems.push(...this.getFeedstockNpkSensItems(feedstock, path));
      }
      if (project.projectInfo.useFinancial) {
        ProjectInputItems.push(...this.getFeedstockFinancialSensItems(feedstock, path));
      }
    }
    return ProjectInputItems;
  }

  getNewPlantSpecInputs(isSensitivity: boolean = false): ProjectInputItem[] {
    return this.getPlantSpecComparisonInputs(this.project, isSensitivity);
  }

  getCurrentResultPlantSpecInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getPlantSpecComparisonInputs(this.currentResultsProject, isSensitivity);
  }

  getLatestResultPlantSpecInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getPlantSpecComparisonInputs(this.latestResultProject, isSensitivity);
  }

  private getPlantSpecComparisonInputs(project: Project, isSensitivity: boolean): ProjectInputItem[] {
    if (!project) return [];
    const ProjectInputItems: ProjectInputItem[] = [];
    const plantSpec = project.plantSpecifications;
    const path = [
      'plantSpecifications'
    ];
    const ignoredInputs = [
      'name',
      'dewateringType',
      'generatedProducts',
      'consumedFuels',
      'description',
      'useNominalCapacity',
      'digesterEfficiencyEnabled',
      'digesterTemperatureEnabled',
      'digesterName'
    ];
    if (isSensitivity) {
      ignoredInputs.push('overheadVolume');
      ignoredInputs.push('digesterTemperature');
    }
    if (!project.plantSpecifications.digesterEfficiencyEnabled) {
      ignoredInputs.push('digesterEfficiency');
    }
    if (!project.plantSpecifications.digesterTemperatureEnabled) {
      ignoredInputs.push('digesterTemperature');
    }
    ProjectInputItems.push(...this.processInputs(
      ignoredInputs,
      plantSpec,
      'plant specifications',
      [ProjectPropName.plantSpecifications],
      path,
      PlantSpecificationsPropName
    ));
    if (plantSpec.selectedUnits.includes('PROJECT.PLANT_SPEC.CHP') && plantSpec.chp.nominalCapacityToggle) {
      ProjectInputItems.push(...this.getSingleEnergyGenScenarioInputs(plantSpec.chp, 'chp', [ProjectPropName.plantSpecifications, 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.CHP'], path));
    }
    if (plantSpec.selectedUnits.includes('PROJECT.PLANT_SPEC.GENERATOR') && plantSpec.generator.nominalCapacityToggle) {
      ProjectInputItems.push(...this.getSingleEnergyGenScenarioInputs(plantSpec.generator, 'generator', [ProjectPropName.plantSpecifications, 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.GENERATOR'], path));
    }
    if (plantSpec.selectedUnits.includes('PROJECT.PLANT_SPEC.BOILER') && plantSpec.boiler.nominalCapacityToggle) {
      ProjectInputItems.push(...this.getSingleEnergyGenScenarioInputs(plantSpec.boiler, 'boiler', [ProjectPropName.plantSpecifications, 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.BOILER'], path));
    }
    if (plantSpec.selectedUnits.includes('PROJECT.PLANT_SPEC.UPGRADER') && plantSpec.upgrader.nominalCapacityToggle) {
      ProjectInputItems.push(...this.getSingleGasUpgraderScenarioInputs(plantSpec.upgrader, 'upgrader', [ProjectPropName.plantSpecifications, 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.UPGRADER'], path));
    }
    ProjectInputItems.push(...this.getGeneratedProductSensInputs(
      plantSpec.generatedProducts,
      isSensitivity,
      'plant specifications, generated products',
      [ProjectPropName.plantSpecifications, PlantSpecificationsPropName.generatedProducts],
      path.concat('generatedProducts')
    ));
    ProjectInputItems.push(...this.getAdditionalProductSensInputs(
      plantSpec.additionalProducts,
      'plant specifications, additional products',
      [ProjectPropName.plantSpecifications, PlantSpecificationsPropName.additionalProducts],
      path.concat('additionalProducts')
    ));
    ProjectInputItems.push(...this.getBiogasShareProjectInputs(
      plantSpec.biogasShareData,
      'plant specifications, biogas share data',
      [ProjectPropName.plantSpecifications, PlantSpecificationsPropName.biogasShareData],
      path.concat('biogasShareData')
    ));
    ProjectInputItems.push(...this.getConsumedFuelSensInputs(
      plantSpec.consumedFuels,
      'plant specifications, consumed fuels',
      [ProjectPropName.plantSpecifications, PlantSpecificationsPropName.consumedFuels],
      path.concat('consumedFuels')
    ));
    return ProjectInputItems;
  }

  getNewFinancialInputs(isSensitivity?: boolean) {
    return this.getFinancialComparisonInputs(this.project, isSensitivity);
  }

  getCurrentResultsFinancialInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getFinancialComparisonInputs(this.currentResultsProject, isSensitivity);
  }

  getLatestResultFinancialInputs(isSensitivity = false): ProjectInputItem[] {
    return this.getFinancialComparisonInputs(this.latestResultProject, isSensitivity);
  }

  private getFinancialComparisonInputs(project: Project, isSensitivity: boolean): ProjectInputItem[] {
    if (!project) return [];
    const ProjectInputItems: ProjectInputItem[] = [];
    ProjectInputItems.push(...this.getFinIncomeItems(project));
    ProjectInputItems.push(...this.getFinCapexItems(project));
    ProjectInputItems.push(...this.getFinOpexItems(project));

    // remove `|| true` to remove all the financial/investment items from scenario comparison
    if (isSensitivity || true) {
      ProjectInputItems.push(...this.getFinInvestmentItems(project));
    }
    return ProjectInputItems;
  }

  getNewGhgInputs(): ProjectInputItem[] {
    return this.getGhgComparisonInputs(this.project);
  }

  getCurrentResultGhgInputs(): ProjectInputItem[] {
    return this.getGhgComparisonInputs(this.currentResultsProject);
  }

  getLatestResultGhgInputs(): ProjectInputItem[] {
    return this.getGhgComparisonInputs(this.latestResultProject);
  }

  private getGhgComparisonInputs(project: Project) {
    if (project && project.ghgEmissions) {
      if (project.projectInfo.useGHG) {
        return [];
      }
    }
    const ghgItemsArray = [];
    const ghgSensItems: ProjectInputItem[] = [];
    const path = ['ghgEmissions'];
    ghgSensItems.push(...this.processInputs(
      [
        'transportations', 'carbonCredit', 'isTransportation', 'isEmissionsCustom',
        'electricEmission', 'natgasEmission', 'fertilizerEmission', 'biomethEmission',
        'landfillEmission', 'ssoEmission', 'mwEmission', 'agriEmission', 'forestEmission',
        'municipalEmission', 'isEmissionCustom', 'landfillPercent', 'incinerationPercent',
        'compostingPercent', 'includeWasteDisposal',
      ],
      project.ghgEmissions,
      'ghg emissions',
      [ProjectPropName.ghgEmissions],
      path,
      ProductPropName
    ));
    ghgSensItems.push(...this.getGhgTransportationSensItems(project));
    ghgSensItems.push(...this.getGhgCarbonCreditSensItems(project));
    if (project && project.ghgEmissions && project.ghgEmissions.isEmissionCustom) {
      ghgSensItems.push(...this.ghgGhgCustomEmissions(project));
    }
    ghgItemsArray.push(ghgSensItems);
    return ghgItemsArray;
  }

  /*
  *  sub feedstock sections ----------------------------------------------------
  */

  private getFeedstockQuantitySensItems(feedstock: Feedstock, pathTo: string[]): ProjectInputItem[] {
    if (feedstock && feedstock.quantities) {
      let ignoredItems = ['isAnimal', 'numAnimals', 'manureProduction'];
      if (feedstock.quantities.isAnimal) {
        ignoredItems = ['mass'];
      }
      const pathToQuant = pathTo.concat(['quantities']);
      return this.processInputs(
        ignoredItems,
        feedstock.quantities,
        feedstock.name + ', quantity',
        [feedstock.name, FeedstockPropName.quantities],
        pathToQuant,
        FeedstockQuantitiesPropName
      );
    } else {
      return [];
    }
  }

  private getFeedstockCharacteristicSensItems(feedstock: Feedstock, pathTo: string[]) {
    if (feedstock && feedstock.characteristics) {
      const pathToMatProp = pathTo.concat('characteristics');
      const ignoreInputs = [
        'densityEnabled',
        'codEnabled',
        'characteristicsEnabled'
      ];
      if (!feedstock.characteristics.densityEnabled) {
        ignoreInputs.push('density');
      }
      if (!feedstock.characteristics.codEnabled) {
        ignoreInputs.push('cod');
      }
      return this.processInputs(
        ignoreInputs,
        feedstock.characteristics,
        feedstock.name + ', characteristics',
        [feedstock.name, FeedstockPropName.characteristics],
        pathToMatProp,
        FeedstockCharacteristicsPropName
      );
    } else {
      return [];
    }
  }

  private getFeedstockBmpPotentialSensItems(feedstock: Feedstock, pathTo: string[], isSensitivity?: boolean) {
    if (feedstock && feedstock.bmpPotential && feedstock.bmpPotential.type) {
      let pathToMatProp = pathTo.concat('bmpPotential');
      const ignoreInputs = [
        'yieldType',
        'type'
      ];
      let bmpObj: any;
      const idPrefix = feedstock.name + ', bmp';
      const nameArray = [feedstock.name, 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ABBR'];
      let currentEnum: any;
      switch (feedstock.bmpPotential.type) {
        case FeedstockBmpType.BMP_DIRECT:
          bmpObj = feedstock.bmpPotential.bmpDirect;
          currentEnum = FeedstockBmpPotentialDirectPropName;
          pathToMatProp = pathToMatProp.concat('bmpDirect');
          break;
        case FeedstockBmpType.BMP_CURVE:
          bmpObj = feedstock.bmpPotential.bmpCurve;
          currentEnum = FeedstockBmpCurvePropName;
          pathToMatProp = pathToMatProp.concat('bmpCurve');
          if (isSensitivity) {
            ignoreInputs.push(
              'bmp',
              'hrt',
              'curve',
            );
          }
          break;
        case FeedstockBmpType.BIOGAS_YIELD:
          bmpObj = feedstock.bmpPotential.bmpYield;
          currentEnum = FeedstockBmpPotentialYieldPropName;
          pathToMatProp = pathToMatProp.concat('bmpYield');
          break;
        case FeedstockBmpType.COD:
          bmpObj = feedstock.bmpPotential.bmpCod;
          currentEnum = FeedstockBmpPotentialCodPropName;
          pathToMatProp = pathToMatProp.concat('bmpCod');
          break;
        case FeedstockBmpType.NUTRIENT_COMPOSITION:
          bmpObj = feedstock.bmpPotential.bmpNutrient;
          currentEnum = FeedstockBmpPotentialNutritionPropName;
          pathToMatProp = pathToMatProp.concat('bmpNutrient');
          if (isSensitivity) {
            ignoreInputs.push(
              'carbohydrate',
              'lipidFat',
              'protein',
            );
          }
          break;
        case FeedstockBmpType.ELEMENTAL:
          bmpObj = feedstock.bmpPotential.bmpElemental;
          currentEnum = FeedstockBmpPotentialElementalPropName;
          pathToMatProp = pathToMatProp.concat('bmpElemental');
          if (isSensitivity) {
            ignoreInputs.push(
              'c',
              'h',
              'n',
              's',
              'o',
            );
          }
          break;
        case FeedstockBmpType.AI1:
          bmpObj = feedstock.bmpPotential.bmpAI1;
          currentEnum = FeedstockBmpPotentialAI1PropName;
          pathToMatProp = pathToMatProp.concat('bmpAI1');
          ignoreInputs.push('ch4ContentEnabled');
          if (!bmpObj.ch4ContentEnabled) {
            ignoreInputs.push('ch4Content');
          }
          if (isSensitivity) {
            ignoreInputs.push(
              'lipidFat',
              'protein',
              'hemicellulose',
              'cellulose',
              'lignin',
              'nonFibreCarbohydrate',
            );
          }
          break;
        case FeedstockBmpType.AI2:
          bmpObj = feedstock.bmpPotential.bmpAI2;
          currentEnum = FeedstockBmpPotentialAI2PropName;
          pathToMatProp = pathToMatProp.concat('bmpAI2');
          ignoreInputs.push('ch4ContentEnabled');
          if (!bmpObj.ch4ContentEnabled) {
            ignoreInputs.push('ch4Content');
          }
          if (isSensitivity) {
            ignoreInputs.push(
              'lipidFat',
              'protein',
              'ndf',
              'adf',
              'adl',
              'nonFibreCarbohydrate',
            );
          }
          break;
        default:
          throw new Error('invalid bmp type given for sensitivity inputs: ' + feedstock.bmpPotential.type);
      }
      return this.processInputs(
        ignoreInputs,
        bmpObj,
        idPrefix,
        nameArray,
        pathToMatProp,
        currentEnum
      );
    } else {
      return [];
    }
  }

  private getFeedstockNpkSensItems(feedstock: Feedstock, pathTo: string[]) {
    if (feedstock && feedstock.npk && feedstock.npkEnabled) {
      const pathToMatProp = pathTo.concat('npk');
      return this.processInputs(
        [],
        feedstock.npk,
        feedstock.name + ', npk',
        [feedstock.name, 'INPUT_SUMMARY.FEEDSTOCK.NPK.ABBR'],
        pathToMatProp,
        FeedstockNpkPropName
      );
    } else {
      return [];
    }
  }

  private getFeedstockFinancialSensItems(feedstock: Feedstock, pathTo: string[]) {
    if (feedstock && feedstock.financial) {
      const fin = feedstock.financial;
      const pathToFeedstockFin = pathTo.concat('financial');
      const inputs: ProjectInputItem[] = [];
      if (fin.feeType !== FinancialFeeType.FREE && fin.price && fin.amount &&
        fin.price.value && fin.price.unit &&
        fin.amount.value && fin.amount.unit
      ) {
        const priceType = (fin.feeType === FinancialFeeType.TIPPING_FEE ?
          'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.TIPPING_FEE' : 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.PRICE');
        const cost = new ProjectInputItem();
        cost.value = fin.price.value;
        cost.unit = this.getUnitText(fin.price.unit) + ' per ' +
          fin.amount.value + ' ' + this.getUnitText(fin.amount.unit);
        cost.id = feedstock.name + ', financial, '
          + (fin.feeType === FinancialFeeType.TIPPING_FEE ? 'tipping fee' : 'price');
        cost.nameElements = [feedstock.name, FeedstockPropName.financial, priceType];
        cost.path = pathToFeedstockFin.concat('price', 'value');
        inputs.push(cost);
      }
      if (fin.useTransport && fin.transportPrice && fin.transportAmount &&
        fin.transportPrice.value && fin.transportPrice.unit &&
        fin.transportAmount.value && fin.transportAmount.unit
      ) {
        const transportPrice = new ProjectInputItem();
        transportPrice.value = fin.transportPrice.value;
        transportPrice.unit = this.getUnitText(fin.transportPrice.unit) +
          ' per ' + fin.transportAmount.value + ' ' +
          this.getUnitText(fin.transportAmount.unit);
        transportPrice.id = feedstock.name + ', financial, transport price';
        transportPrice.nameElements = [feedstock.name, FeedstockPropName.financial, FeedstockFinancialPropName.transportPrice];
        transportPrice.path = pathToFeedstockFin.concat('transportPrice', 'value');
        inputs.push(transportPrice);
      }
      const ignoreInputs = [
        'feeType',
        'price',
        'amount',
        'useTransport',
        'transportPrice',
        'transportAmount'
      ];
      if (feedstock.financial.feeType === FinancialFeeType.FREE) {
        ignoreInputs.push('feedstockCpi');
      }
      if (!feedstock.financial.useTransport) {
        ignoreInputs.push('transportCpi');
      }
      inputs.push(...this.processInputs(
        ignoreInputs,
        fin,
        feedstock.name + ', financial',
        [feedstock.name, FeedstockPropName.financial],
        pathToFeedstockFin,
        FeedstockFinancialPropName
      ));
      return inputs;
    }
  }

  /*
  *  sub plant specifications sections ----------------------------------------------------
  */

  private getGeneratedProductSensInputs(products: Product[], isSensitivity: boolean, idPrefix: string, nameArray: string[], pathTo: string[]) {
    const productInputs: ProjectInputItem[] = [];

    for (const product of products) {
      const curPath = pathTo.concat(product.getUuidForPath());

      // Don't include price in inputs if using variable price structure
      if (!product.priceIsVariable
        && product.price && product.price.value && product.price.unit
        && product.quantity && product.quantity.value && product.quantity.unit
      ) {
        const price = new ProjectInputItem();
        price.value = product.price.value;
        price.unit = this.getUnitText(product.price.unit) + ' per ' +
          product.quantity.value + ' ' + this.getUnitText(product.quantity.unit);
        price.id = idPrefix + ', ' + product.name + ', price';
        price.nameElements = nameArray.concat([product.name, 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.PRICE']);
        price.path = curPath.concat('price', 'value');
        productInputs.push(price);
      }

      const ignoredInputs = [
        'description',
        'price',
        'quantity',
        'period',
        'generatedProductPercentage',
        'priceIsLcfsRin',
        'priceIsVariable',
        'priceStructures',
        'rangeOf',
        'ispricechanged'
      ];

      if (product.priceIsVariable) {
        ignoredInputs.push('cpi');
      }

      productInputs.push(...this.processInputs(
        ignoredInputs,
        product,
        idPrefix + ', ' + product.name,
        nameArray.concat([product.name]),
        curPath,
        ProductPropName
      ));

      if (!isSensitivity && (product.priceIsLcfsRin && product.canCalculateLcfsrin()) && !product.priceIsVariable) {
        const ignored = ['cwcPrice', 'lhvCh4'];
        if (product.lcfsRin.rinEnabled) {
          if (product.lcfsRin.rinD3 && product.lcfsRin.rinD3.hasValues()) {
            ignored.push('rinD5');
          }
          if (product.lcfsRin.rinD5 && product.lcfsRin.rinD5.hasValues()) {
            ignored.push('rinD3');
          }
        } else {
          ignored.push(...['rinD5', 'rinD3']);
        }
        if (!product.lcfsRin.lcfsEnabled) {
          ignored.push(...['carbonBaseline', 'carbonIntensity', 'averagePrice']);
        }
        if (!product.lcfsRin.natgasEnabled) {
          ignored.push('natgasCommodity');
        }
        productInputs.push(...this.processInputs(
          ignored,
          product.lcfsRin,
          [idPrefix, product.name, ProductPropName.id_lcfsRin].join(', '),
          nameArray.concat([product.name, ProductPropName.lcfsRin]),
          curPath.concat('lcfsRin'),
          LcfsRinPropName
        ));
      }

    }
    return productInputs;
  }

  private getAdditionalProductSensInputs(additionalProducts: AdditionalProduct[], prefix: string, nameArray: string[], pathTo: string[]) {
    const additionalProductInputs: ProjectInputItem[] = [];

    for (const additionalProduct of additionalProducts) {
      const curPath = pathTo.concat(additionalProduct.getUuidForPath());

      if (additionalProduct.price &&
        additionalProduct.price.value && additionalProduct.price.unit
      ) {
        const price = new ProjectInputItem();
        price.value = additionalProduct.price.value;
        price.unit = this.getUnitText(additionalProduct.price.unit) + ' per ' +
          additionalProduct.pricePer.value + ' ' + this.getUnitText(additionalProduct.pricePer.unit);
        price.id = prefix + ', ' + additionalProduct.name + ', price';
        price.nameElements = nameArray.concat([additionalProduct.name, 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.PRICE']);
        price.path = curPath.concat('price', 'value');
        additionalProductInputs.push(price);
      }

      if (additionalProduct.quantity && additionalProduct.quantityPer &&
        additionalProduct.quantity.value && additionalProduct.quantityPer.unit &&
        additionalProduct.quantity.value && additionalProduct.quantityPer.unit
      ) {
        const quantity = new ProjectInputItem();
        quantity.value = additionalProduct.quantity.value;
        if (Utils.doesLookLikeUUID(additionalProduct.parentProduct)) {
          additionalProduct.parentProduct = this.projectCommService.project.plantSpecifications.getParentProductNameFromUUID(additionalProduct.parentProduct);
        }
        quantity.unit = this.getUnitText(additionalProduct.quantity.unit) + ' per ' +
          additionalProduct.quantityPer.value + ' ' + this.getUnitText(additionalProduct.quantityPer.unit) + ' ' + additionalProduct.parentProduct.replace(' overall', '');
        quantity.id = prefix + ', ' + additionalProduct.name + ', quantity';
        quantity.nameElements = nameArray.concat([additionalProduct.name, AddProdPropName.quantity]);
        quantity.path = curPath.concat('quantity', 'value');
        additionalProductInputs.push(quantity);
      }

      additionalProductInputs.push(...this.processInputs(
        [
          'description',
          'price',
          'pricePer',
          'quantity',
        ],
        additionalProduct,
        prefix + ', ' + additionalProduct.name,
        nameArray.concat(additionalProduct.name),
        curPath,
        AddProdPropName
      ));
    }
    return additionalProductInputs;
  }

  private getBiogasShareProjectInputs(
    biogasShareData: BiogasShare[],
    prefix: string,
    nameArray: string[],
    pathTo: string[]
  ): ProjectInputItem[] {

    const biogasShareInputs: ProjectInputItem[] = [];

    for (const biogasShareItem of biogasShareData) {

    }

    return [];
  }

  private getConsumedFuelSensInputs(fuels: Fuel[], prefix: string, nameArray: string[], pathTo: string[]) {
    const fuelInputs: ProjectInputItem[] = [];

    for (const fuel of fuels) {
      const curPath = pathTo.concat(fuel.getUuidForPath());

      if (fuel.cost && fuel.amount &&
        fuel.cost.value && fuel.cost.unit &&
        fuel.amount.value && fuel.amount.unit
      ) {
        const cost = new ProjectInputItem();
        cost.value = fuel.cost.value;
        cost.unit = this.getUnitText(fuel.cost.unit) + ' per ' +
          fuel.amount.value + ' ' + this.getUnitText(fuel.amount.unit);
        cost.id = prefix + ', ' + fuel.name + ', price';
        cost.nameElements = nameArray.concat([fuel.name, 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.PRICE']);
        cost.path = curPath.concat('cost', 'value');
        fuelInputs.push(cost);
      }

      if (fuel.quantity && fuel.period &&
        fuel.quantity.value && fuel.period.unit &&
        fuel.quantity.value && fuel.period.unit
      ) {
        const cost = new ProjectInputItem();
        cost.value = fuel.quantity.value;
        cost.unit = this.getUnitText(fuel.quantity.unit) + ' per ' +
          fuel.period.value + ' ' + this.getUnitText(fuel.period.unit);
        cost.id = prefix + ', ' + fuel.name + ', quantity';
        cost.nameElements = nameArray.concat([fuel.name, FuelPropName.quantity]);
        cost.path = curPath.concat('quantity', 'value');
        fuelInputs.push(cost);
      }

      fuelInputs.push(...this.processInputs(
        [
          'typeName',
          'description',
          'cost',
          'amount',
          'quantity',
          'typeID'
        ],
        fuel,
        prefix + ', ' + fuel.name,
        nameArray.concat(fuel.name),
        curPath,
        FuelPropName
      ));
    }
    return fuelInputs;
  }

  private getSingleEnergyGenScenarioInputs(egu: EnergyGenerationUnit, idPrefix: string, nameArray: string[], pathTo: string[]) {
    const inputs: ProjectInputItem[] = [];

    const ignoredInputs = [
      'uuid',
      'name',
      'unitType',
      'electricalEfficiency',
      'thermalEfficiency'
    ];

    const pathToQuant = pathTo.concat([idPrefix]);
    inputs.push(...this.processInputs(
      ignoredInputs,
      egu,
      idPrefix,
      nameArray,
      pathToQuant,
      EnergyGenPropName
    ));

    return inputs;
  }

  private getSingleGasUpgraderScenarioInputs(egu: GasUpgradingUnit, idPrefix: string, nameArray: string[], pathTo: string[]) {
    const inputs: ProjectInputItem[] = [];

    const ignoredInputs = [
      'uuid',
      'name',
      'methaneRecovery'
    ];

    const pathToQuant = pathTo.concat([idPrefix]);
    inputs.push(...this.processInputs(
      ignoredInputs,
      egu,
      idPrefix,
      nameArray,
      pathToQuant,
      GasUpgradingPropName
    ));

    return inputs;
  }

  /*
  *  sub financial sections ----------------------------------------------------
  */

  private getFinIncomeItems(project: Project): ProjectInputItem[] {
    const items: ProjectInputItem[] = [];
    const path = ['financial', 'income'];
    let incomeList: FinancialIncome[] = [];
    // include additional income inputs
    if (project && project.financial) {
      incomeList = project.financial.income;
    }
    for (const income of incomeList) {
      items.push(...this.processInputs(
        ['description', 'name', 'period'],
        income,
        ProjectPropName.id_financial + ', ' + FinancialPropName.id_income + ', ' + income.name,
        [ProjectPropName.financial, FinancialPropName.income, income.name],
        path.concat(income.getUuidForPath()),
        FinancialIncomePropName
      ));
    }
    return items;
  }

  private getFinCapexItems(project: Project) {
    const items: ProjectInputItem[] = [];
    const path = ['financial', 'capex'];
    let capexList: FinancialCapex[] = [];
    if (project && project.financial) {
      capexList = project.financial.capex;
    }
    for (const capex of capexList) {
      items.push(...this.processInputs(
        ['description', 'name', 'uuid', 'estimated', 'connectionType', 'connectionToggle', 'period'],
        capex,
        ProjectPropName.id_financial + ', ' + FinancialPropName.id_capex + ', ' + capex.name,
        [ProjectPropName.financial, FinancialPropName.capex, capex.name],
        path.concat(capex.getUuidForPath()),
        FinCapexPropName
      ));
    }
    return items;
  }

  private getFinOpexItems(project: Project) {
    const items: ProjectInputItem[] = [];
    const path = ['financial', 'opex'];
    let opexList: FinancialOpex[] = [];
    if (project && project.financial) {
      opexList = project.financial.opex;
    }
    for (const opex of opexList) {
      items.push(...this.processInputs(
        ['description', 'name', 'type', 'estimated', 'duration', 'connectionType'],
        opex,
        ProjectPropName.id_financial + ', ' + FinancialPropName.id_opex + ', ' + opex.name,
        [ProjectPropName.financial, FinancialPropName.opex, opex.name],
        path.concat(opex.getUuidForPath()),
        FinOpexPropName
      ));
    }
    return items;
  }

  private getFinInvestmentItems(project: Project) {
    const items: ProjectInputItem[] = [];
    const path = ['financial', 'investment'];
    let investment: FinancialInvestment;
    if (project && project.financial) {
      investment = project.financial.investment;
    }

    // add 'loans' to ignore it
    const ignoredInputs = [
      'discountToggle',
      'dividendsToggle',
      'incomeToggle',
      'valueToggle',
      'contributionsUseCurrency',
      'distributions',
      'partnerships'
    ];

    // Ignore investment items if disabled
    if (!investment.discountToggle) {
      ignoredInputs.push('discountRate');
    }
    if (!investment.incomeToggle) {
      ignoredInputs.push('incomeTax');

    }
    if (!investment.valueToggle) {
      ignoredInputs.push('residualValue');

    }
    items.push(...this.processInputs(
      ignoredInputs,
      investment,
      'financial, investment',
      [ProjectPropName.financial, FinancialPropName.investment],
      path,
      FinancialInvestmentPropName
    ));
    if (!ignoredInputs.includes('loans')) {
      items.push(...this.getInvestmentLoansSensInputs(
        investment.loans,
        ProjectPropName.id_financial + ', ' + FinancialPropName.id_investment + ', ' + FinancialInvestmentPropName.id_loans,
        [ProjectPropName.financial, FinancialPropName.investment, FinancialInvestmentPropName.loans],
        path.concat('loans')
      ));
    }
    return items;
  }

  private getInvestmentLoansSensInputs(loans: Loan[], prefix: string, nameArray: string[], pathTo: string[]) {
    const loanInputs: ProjectInputItem[] = [];

    for (const loan of loans) {
      const curPath = pathTo.concat(loan.getUuidForPath());

      if (loan.loanAmount && loan.loanAmount.value >= 0 && loan.loanAmount.unit) {
        const loanAmount = new ProjectInputItem();
        loanAmount.value = loan.loanAmount.value;
        loanAmount.unit = this.getUnitText(loan.loanAmount.unit);
        loanAmount.id = prefix + ', ' + loan.name + ', ' + 'loan amount';
        loanAmount.nameElements = nameArray.concat([loan.name, LoanPropName.loanAmount]);
        loanAmount.path = curPath.concat('loanAmount', 'value');
        loanInputs.push(loanAmount);
      }

      if (!loan.isForgiven()) {
        if (loan.interestRate && loan.interestRate.value >= 0 && loan.interestRate.unit) {
          const interestRate = new ProjectInputItem();
          interestRate.value = loan.interestRate.value;
          interestRate.unit = this.getUnitText(loan.interestRate.unit);
          interestRate.id = prefix + ', ' + loan.name + ', ' + 'interest rate';
          interestRate.nameElements = nameArray.concat([loan.name, LoanPropName.interestRate]);
          interestRate.path = curPath.concat('interestRate', 'value');
          loanInputs.push(interestRate);
        }
      }

      // UNCOMMENT below code to include amortization periods of loans in SCENARIO COMPARISON
      // if (loan.amortizationPeriod && loan.amortizationPeriod.value && loan.amortizationPeriod.unit) {
      //   const amortizationPeriod = new ComparisonInput();
      //   amortizationPeriod.value = loan.amortizationPeriod.value;
      //   amortizationPeriod.unit = this.getUnitText(loan.amortizationPeriod.unit);
      //   amortizationPeriod.id = prefix + ', ' + loan.name + ', ' + 'amortization period';
      //   amortizationPeriod.nameElements = nameArray.concat([loan.name, LoanPropName.amortizationPeriod]);
      //   amortizationPeriod.path = curPath.concat('amortizationPeriod', 'value');
      //   loanInputs.push(amortizationPeriod);
      // }
    }
    return loanInputs;
  }

  /*
  *  sub ghgEmissions sections -------------------------------------------------
  */

  private getGhgTransportationSensItems(project: Project) {
    const transportItemsArray: ProjectInputItem[] = [];
    const path = ['ghgEmissions', 'transportations'];
    if (!project || project.ghgEmissions || project.ghgEmissions.transportations) {
      throw Error('unable to get ghg transportation from invalid project');
    }
    let counter = 1;
    for (const transport of project.ghgEmissions.transportations) {
      const sensInputItems: ProjectInputItem[] = [];
      const pathTo = path.concat(transport.getUuidForPath());
      if (transport.fuelQuantity && transport.fuelPer &&
        transport.fuelQuantity.value && transport.fuelPer.unit &&
        transport.fuelQuantity.value && transport.fuelPer.unit
      ) {
        const fuelQuant = new ProjectInputItem();
        fuelQuant.value = transport.fuelQuantity.value;
        fuelQuant.unit = this.getUnitText(transport.fuelQuantity.unit) + ' per ' +
          transport.fuelPer.value + ' ' + this.getUnitText(transport.fuelPer.unit);
        fuelQuant.id = 'greenhouse gas emissions, transportation ' + counter + ', ' + transport.name + ', fuel consumed';
        fuelQuant.nameElements = [ProjectPropName.ghgEmissions, GhgEmissionsPropName.transportations,
          counter + '', transport.name, TransportPropName.fuelQuantity];
        fuelQuant.path = pathTo.concat('fuelQuantity', 'value');
        sensInputItems.push(fuelQuant);
      }

      if (transport.distanceChange && transport.distancePer &&
        transport.distanceChange.value && transport.distancePer.unit &&
        transport.distanceChange.value && transport.distancePer.unit
      ) {
        const distanceChange = new ProjectInputItem();
        distanceChange.value = transport.distanceChange.value;
        distanceChange.unit = this.getUnitText(transport.distanceChange.unit) + ' per ' +
          transport.distancePer.value + ' ' + this.getUnitText(transport.distancePer.unit);
        const changeType = (transport.isIncrease ? 'SENSITIVITY.INPUTS.DISTANCE_INCREASE' : 'SENSITIVITY.INPUTS.DISTANCE_DECREASE');
        const changeId = (transport.isIncrease ? 'increase' : 'decrease');
        distanceChange.id = 'greenhouse gas emissions, transportation ' + counter + ', ' +
          transport.name + ', distance ' + changeId;
        distanceChange.nameElements = [ProjectPropName.ghgEmissions, GhgEmissionsPropName.transportations, '' + counter,
          transport.name, changeType];
        distanceChange.path = pathTo.concat('distanceChange', 'value');
        sensInputItems.push(distanceChange);
      }
      sensInputItems.push(...this.processInputs(
        ['isIncrease', 'fuelQuantity', 'fuelPer', 'distanceChange', 'distancePer'],
        transport,
        'greenhouse gas emissions, transportation ' + counter + ', ' + transport.name,
        [ProjectPropName.ghgEmissions, GhgEmissionsPropName.transportations, '' + counter, transport.name],
        pathTo,
        TransportPropName
      ));
      transportItemsArray.push(...sensInputItems);
      counter++;
    }
    return transportItemsArray;
  }

  private getGhgCarbonCreditSensItems(project: Project) {
    const carbonCreditArray: ProjectInputItem[] = [];

    const cccPrice = project.ghgEmissions.carbonCredit.customPrice;
    if (cccPrice && cccPrice.price && cccPrice.pricePer &&
      cccPrice.price.value && cccPrice.pricePer.unit &&
      cccPrice.price.value && cccPrice.pricePer.unit
    ) {
      const price = new ProjectInputItem();
      const pricePath = ['ghgEmissions', 'carbonCredit', 'customPrice', 'price', 'value'];
      price.value = cccPrice.price.value;
      price.unit = this.getUnitText(cccPrice.price.unit) + ' per ' +
        cccPrice.pricePer.value + ' ' + this.getUnitText(cccPrice.pricePer.unit);
      price.id = 'greenhouse gas emissions, carbon credit, price';
      price.nameElements = [ProjectPropName.ghgEmissions, GhgEmissionsPropName.carbonCredit, 'SENSITIVITY.INPUTS.PRICE'];
      price.path = pricePath;
      carbonCreditArray.push(price);
    }

    carbonCreditArray.push(...this.processInputs(
      [
        'includeCarbonCredit',
        'inputType',
        'databasePriceCollection',
        'databasePriceName',
        'databasePriceReference',
        'customPrice'
      ],
      project.ghgEmissions.carbonCredit,
      'greenhouse gas emissions, carbon credit',
      [ProjectPropName.ghgEmissions, GhgEmissionsPropName.carbonCredit],
      ['ghgEmissions', 'carbonCredit'],
      CarbonCreditPropName
    ));
    return carbonCreditArray;
  }

  private ghgGhgCustomEmissions(project: Project) {
    const cccEmissionInputs: ProjectInputItem[] = [];
    if (project && project.ghgEmissions && project.ghgEmissions.isEmissionCustom) {
      const ghg = project.ghgEmissions;
      const inputs: { id: string, name: string[], path: string[], value: number, unit: string }[] = [];
      const prefix = 'greenhouse gas emissions, custom emission, ';
      Object.keys(ghg).forEach((key) => {
        if (ghg[key] instanceof EmissionRef) {
          const path = ['ghgEmissions', key, 'customEmission'];
          const emiRef = new EmissionRef(ghg[key]);
          inputs.push(...emiRef.customEmission.getSensInputs(
            prefix + this.emissionNames['id_' + key],
            [ProjectPropName.ghgEmissions, GhgEmissionsPropName.isEmissionCustom,
              this.emissionNames[key]],
            path
          ));
        }
      });
      if (project.plantSpecifications &&
        project.plantSpecifications.generatedProducts
      ) {
        const genProds = project.plantSpecifications.generatedProducts;
        for (const prod of genProds) {
          if (prod.type !== ProductType.Electric && prod.type !== ProductType.Heat) {
            const path = ['plantspecifcations', 'generatedProducts', '' + genProds.indexOf(prod),
              'emission', 'customEmission'];
            const emiRef = new EmissionRef(prod.emission);
            inputs.push(...emiRef.customEmission.getSensInputs(
              prefix + prod.name,
              [ProjectPropName.ghgEmissions, GhgEmissionsPropName.isEmissionCustom, prod.name],
              path
            ));
          }
        }
      }
      for (const input of inputs) {
        const sensInput = new ProjectInputItem();
        sensInput.value = input.value;
        sensInput.unit = this.getUnitText(input.unit);
        sensInput.id = input.id;
        sensInput.path = input.path;
        cccEmissionInputs.push(sensInput);
      }
    }
    return cccEmissionInputs;
  }

  /*
  *  general functionality sections --------------------------------------------
  */

  private processInputs(
    ignoredInputs: string[],
    inputs: any,
    idPrefix: string,
    nameArray: string[],
    pathTo: string[],
    propEnum: any,
  ): ProjectInputItem[] {

    const inputsArray: ProjectInputItem[] = [];
    Object.keys(inputs).forEach((key) => {
      if (!ignoredInputs.includes(key)) {
        if (inputs[key]) {
          if (Utils.isNumeric(inputs[key].value)) {
            const objPathTo = pathTo.concat([key, 'value']);
            const inputName: string[] = [this.getNameFromEnum(propEnum, key)];
            const id = idPrefix + ', ' + this.getNameFromEnum(propEnum, 'id_' + key);
            const newSensItem = new ProjectInputItem();
            newSensItem.id = id;
            if (inputs instanceof PlantSpecifications) {
              this.addPlantSpecSectionName(key, inputName, inputs.selectedUnits);
            } else if (((inputs instanceof Fuel || inputs instanceof FinancialOpex) && key === 'period') ||
              (inputs instanceof FinancialCapex && key === 'quantity' && inputs.connectionToggle)) {
              this.convertNameToConnectionType(inputs, inputName);
            }
            newSensItem.nameElements = nameArray.concat(inputName);
            newSensItem.value = inputs[key].value;
            if (newSensItem.id.includes('additional products')) {
              if (Utils.doesLookLikeUUID(inputs['parentProduct'])) {
                inputs['parentProduct'] = this.projectCommService.project.plantSpecifications.getParentProductNameFromUUID(inputs['parentProduct']);
              }
              newSensItem.unit = this.getUnitText(inputs[key].unit) + ' ' + inputs['parentProduct'].replace(' overall', '');
            } else {
              newSensItem.unit = this.getUnitText(inputs[key].unit);
            }
            newSensItem.path = objPathTo;
            if (newSensItem.unit === 'SENSITIVITY.INPUT_SELECTION.N_A' || newSensItem.value === null) {
              return;
            }
            inputsArray.push(newSensItem);
          }
        } else {
          console.error(
            'input with the key "' + key + '", and the prefix "' + idPrefix + '" is undefined, ' +
            'should this field be added to an ignored inputs array in ' +
            'the project service?'
          );
          console.error(pathTo);
        }
      }
    });
    this.isBmpCurveAddCoefficient(inputsArray, inputs, idPrefix, nameArray, pathTo, propEnum);
    return inputsArray;
  }

  private convertNameToConnectionType(inputs, inputName) {
    const type = inputs['perType'] || inputs['connectionType'];
    if (type) {
      switch (type) {
        case PeriodType.ELECTRIC:
          inputName[0] = 'PRODUCTS.ELECTRICITY';
          break;
        case PeriodType.HEAT:
          inputName[0] = 'PRODUCTS.HEAT';
          break;
        case PeriodType.BIOMETHANE:
          inputName[0] = 'PRODUCTS.BIOMETHANE';
          break;
        case PeriodType.WHOLE:
          inputName[0] = 'PRODUCTS.WDBF';
          break;
        case PeriodType.THIN:
          inputName[0] = 'PRODUCTS.TDBF';
          break;
        case PeriodType.SOLID:
          inputName[0] = 'PRODUCTS.SDBF';
          break;
        case PeriodType.FEEDAMOUNT:
          inputName[0] = 'PROJECT.PLANT_SPEC.FEED_AMOUNT';
          break;
        case PeriodType.BIOGAS:
          inputName[0] = 'PRODUCTS.BIOGAS';
          break;
        default:
          break;
      }
    }
  }

  private addPlantSpecSectionName(key: string, inputName: string[], selectedUnits: string[]) {
    switch (key) {
      case 'overheadVolume':
      case 'targetTS':
      case 'digesterEfficiency':
      case 'digesterTemperature':
      case 'workingDays':
      case 'periodOLR':
      case 'periodHRT':
      case 'overallWorkingVolume':
        inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.DIGESTER');
        break;
      case 'recovery':
      case 'recycle':
      case 'cakeDryness':
        inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.DEWATERING');
        break;
      case 'thermalEfficiency':
      case 'electricalEfficiency':
        if (selectedUnits.includes('PROJECT.PLANT_SPEC.CHP')) {
          inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.CHP');
        } else {
          if (key === 'thermalEfficiency') {
            inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.BOILER');
          } else {
            inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.GENERATOR');
          }
        }
        break;
      case 'methaneRecovery':
        inputName.unshift('INPUT_SUMMARY.PLANT_SPECIFICATIONS.UPGRADER');
        break;
      default:
        break;
    }
  }

  private getUnitText(unitId: string): string {
    if (unitId) {
      const unit = this.unitService.getUnitById(unitId);
      if (unit) {
        return unit.translationId ? unit.translationId : unit.displayName;
      }
    }
    return 'SENSITIVITY.INPUT_SELECTION.N_A';
  }

  private getNameFromEnum(propEnum: any, key: string) {
    if (propEnum && propEnum[key]) {
      return propEnum[key];
    }
    return 'SENSITIVITY.INPUT_SELECTION.N_A';
  }

  private addBmpCurveCoefficient(
    inputs: any,
    idPrefix: string,
    nameArray: string[],
    pathTo: string[],
    propEnum: any
  ): ProjectInputItem {

    const newSensItem = new ProjectInputItem();
    const curve: FeedstockBmpCurveItem[] = inputs.curve;
    const objPathTo = pathTo.concat(['curve coefficient', 'value']);
    const inputName: string[] = [this.getNameFromEnum(propEnum, 'curve_coefficient')];
    const id = idPrefix + ', curve coefficient';
    newSensItem.id = id;
    newSensItem.nameElements = nameArray.concat(inputName);
    newSensItem.value = 1;
    newSensItem.unit = '-';
    newSensItem.curve = curve;
    // newSensItem.selected = false;
    newSensItem.path = objPathTo;

    return newSensItem;
  }

  private isBmpCurveAddCoefficient(
    inputsArray: ProjectInputItem[],
    inputs: any,
    idPrefix: string,
    nameArray: string[],
    pathTo: string[],
    propEnum: any
  ): ProjectInputItem[] {

    if (inputs.curve && inputs.curve.length > 0) {
      inputsArray.push(this.addBmpCurveCoefficient(inputs, idPrefix, nameArray, pathTo, propEnum));
      return inputsArray;
    }

    return inputsArray;
  }
}
